import React, { useState } from 'react';
import { LayoutChangeEvent, View } from 'react-native';
import Svg, { Circle, Line, Rect } from 'react-native-svg';

import { useThemePalette } from '@/config/theme';

/**
 * Crisp white markings for a TOP-DOWN (vertical) formation pitch — attack at the
 * TOP, goalkeeper at the BOTTOM. Drawn in one react-native-svg node, in the SAME
 * geometry language as the horizontal SVG pitches (XgPanel / AveragePositions
 * FullPitch): a flat-cornered penalty box at each goal-line edge, the smaller
 * 6-yard goal area inside it, a thin goal line, a penalty spot + arc, plus the
 * halfway line, centre circle and spot.
 *
 * Self-measures via onLayout and fills its parent (`absolute inset-0`), so a
 * caller drops it in exactly like the old NativeWind markings — no width/height
 * props needed. Sized in pixels off the measured box so the box is always a real,
 * shallow rectangle (penalty box WIDE but SHALLOW at the top edge) instead of the
 * mis-rounded `rounded-b-lg` shape it replaces.
 */
export function VerticalPitchMarkings() {
  const palette = useThemePalette();
  const line = palette.pitchLine;
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  const onLayout = (e: LayoutChangeEvent) =>
    setSize({ w: e.nativeEvent.layout.width, h: e.nativeEvent.layout.height });

  const { w, h } = size;

  return (
    <View className="absolute inset-0" style={{ pointerEvents: 'none' }} onLayout={onLayout}>
      {w > 0 && h > 0 ? <Markings width={w} height={h} line={line} /> : null}
    </View>
  );
}

/** The actual SVG, sized to the measured box. */
function Markings({ width, height, line }: { width: number; height: number; line: string }) {
  const cx = width / 2;
  const cy = height / 2;
  // Penalty box: wide but shallow at the goal edge. Goal area sits inside it.
  const boxW = Math.min(width * 0.62, 240);
  const boxH = Math.min(height * 0.18, 56);
  const goalAreaW = boxW * 0.46;
  const goalAreaH = boxH * 0.46;
  const goalW = goalAreaW * 0.62;
  const goalDepth = Math.max(4, boxH * 0.16);
  const boxLeft = cx - boxW / 2;
  const goalAreaLeft = cx - goalAreaW / 2;
  const goalLeft = cx - goalW / 2;
  const penFromGoal = boxH * 0.66; // penalty spot, in from the goal line
  const centreR = Math.min(width * 0.18, 46);
  const r = 1.6;

  return (
    <Svg width={width} height={height} pointerEvents="none">
      {/* Halfway line + centre circle + spot */}
      <Line x1={0} y1={cy} x2={width} y2={cy} stroke={line} strokeWidth={2} opacity={0.55} />
      <Circle cx={cx} cy={cy} r={centreR} fill="none" stroke={line} strokeWidth={2} opacity={0.5} />
      <Circle cx={cx} cy={cy} r={r} fill={line} opacity={0.7} />

      {/* TOP end (attacking): goal line, penalty box, goal area, spot. */}
      <Rect x={goalLeft} y={-goalDepth} width={goalW} height={goalDepth} fill="none" stroke={line} strokeWidth={2} opacity={0.7} />
      <Rect x={boxLeft} y={0} width={boxW} height={boxH} fill="none" stroke={line} strokeWidth={2} opacity={0.6} />
      <Rect x={goalAreaLeft} y={0} width={goalAreaW} height={goalAreaH} fill="none" stroke={line} strokeWidth={2} opacity={0.5} />
      <Circle cx={cx} cy={penFromGoal} r={r} fill={line} opacity={0.7} />

      {/* BOTTOM end (defending): goal line, penalty box, goal area, spot. */}
      <Rect x={goalLeft} y={height} width={goalW} height={goalDepth} fill="none" stroke={line} strokeWidth={2} opacity={0.7} />
      <Rect x={boxLeft} y={height - boxH} width={boxW} height={boxH} fill="none" stroke={line} strokeWidth={2} opacity={0.6} />
      <Rect x={goalAreaLeft} y={height - goalAreaH} width={goalAreaW} height={goalAreaH} fill="none" stroke={line} strokeWidth={2} opacity={0.5} />
      <Circle cx={cx} cy={height - penFromGoal} r={r} fill={line} opacity={0.7} />
    </Svg>
  );
}
